// v4 gas station — a striped hazard-yellow canopy on steel posts sheltering
// a red pump on a concrete island, with a horizontal steel fuel tank at the
// back of the lot. No work station — trucks serve themselves.
//
// Ported 1:1 from age-of-mcp's v4/gas_station.ts, using the library's shared
// v3 style helpers (`styleV3`).
import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl, poles, slab, CANVAS, HAZARD, ROOF_LOGI, ROOF_TRADE, STEEL, STONE } = styleV3;

const BASE = 0.12;            // foundation slab top

// The red pump on its concrete island, with display panel, hose and nozzle.
function pump(): Part[] {
  return [
    box(0.36, 0.05, 0.26, STONE, [0, BASE + 0.025, 0.08]),
    box(0.15, 0.36, 0.13, ROOF_TRADE, [-0.05, BASE + 0.23, 0.08]),
    box(0.11, 0.09, 0.02, CANVAS, [-0.05, BASE + 0.33, 0.155]),
    cyl(0.018, 0.018, 0.16, 5, HAZARD, [0.07, BASE + 0.2, 0.1], [0, 0, 0.5]),
    box(0.04, 0.07, 0.04, HAZARD, [0.12, BASE + 0.12, 0.1]),
  ];
}

// The two steel posts carrying the canopy.
function posts(): Part[] {
  return [
    cyl(0.028, 0.028, 0.62, 6, STEEL, [-0.24, BASE + 0.31, -0.1]),
    cyl(0.028, 0.028, 0.62, 6, STEEL, [0.24, BASE + 0.31, -0.1]),
  ];
}

// The flat hazard-yellow canopy with its dark striped fascia — the icon.
function canopy(): Part[] {
  return [
    box(0.62, 0.05, 0.5, ROOF_LOGI, [0, BASE + 0.65, 0.02]),
    box(0.62, 0.07, 0.04, HAZARD, [0, BASE + 0.60, 0.27]),
    box(0.62, 0.07, 0.04, HAZARD, [0, BASE + 0.60, -0.23]),
  ];
}

// The horizontal steel fuel tank at the back of the lot.
function tank(): Part {
  return cyl(0.13, 0.13, 0.3, 10, STEEL, [-0.16, BASE + 0.14, -0.15], [Math.PI / 2, 0, 0]);
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.5, 0.5)];
  if (s === 1) return [slab(0.5, 0.5), tank(), ...poles(0.44, 0.44, 0.5)];
  if (s === 2) return [slab(0.5, 0.5), tank(), ...pump(), ...posts(), ...poles(0.44, 0.44, 0.8)];
  return [slab(0.5, 0.5), tank(), ...pump(), ...posts(), ...canopy()];
}

export const gasStationV4 = defineModel({
  id: "gas-station-v4",
  tiles: { w: 1, h: 1 },
  batchId: "polished",
  name: "Gas station (v4)",
  tags: ["building","gas","station","low-poly","age-of-mcp","v4"],
  summary: "Low-poly gas station.",
  description: "A fuel pump where vehicles refuel, holding a stock of fuel.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
